<script setup>
const fullWidthMode = inject("fullWidthMode", false);
const halfCol = computed(() => (fullWidthMode ? undefined : 6));
import { useAnalysisStore } from '@/store/modules/analysis';
import {
  removeAsterisk,
  splitKeywordParts,
  splitBySentence,
  splitSentencesByDivider,
  splitCommaSeparatedWords,
} from '@/composables/splitSentence';
import PersonalPieChart from './PersonalPieChart.vue';
import PersonalityKeyword from './PersonalityKeyword.vue';
import NcodeLetters from '@/components/saju-insight/common/NcodeLetters.vue';
import NcodeDescribe from './NcodeDescribe.vue';
import SajuFiveChart from '@/components/saju-insight/ohaeng/SajuFiveChart.vue';
import DivergingBarChart from './DivergingBarChart.vue';

const props = defineProps({
  pData: Object,
  userName: String,
  isMobile: Boolean,
});
const store = useAnalysisStore();
// const userName = computed(() => store.userName)
onMounted(() => {
  // pData.value = store.analysisResult;
});
const showTooltip = ref(false);
/** 코드 쌍 별 계산 */
// TODO: util 분리 고려
const getNCodeChartData = (raw) => {
  const result = {};
  const pairs = {
    ei_rto: ['E', 'I'],
    ns_rto: ['N', 'S'],
    ft_rto: ['T', 'F'],
    pj_rto: ['P', 'J'],
  };
  for (const key in pairs) {
    const [a, b] = pairs[key];
    const [dominant, value] = raw[key];
    result[dominant] = value;
    result[dominant === a ? b : a] = +(100 - value).toFixed(2);
  }
  return result;
};

// 차트 데이터
const nCodeData = computed(() => props.pData.낭만성향.성향분포.NCODE_RTOS);
const nCodeChartData = getNCodeChartData(nCodeData.value);
const fivePieChart = computed(() => props.pData.오행분석.지장비율);

const myNcode = computed(() => props.pData.낭만성향.코드요약);
const similarNcode = computed(() => props.pData.낭만성향.유사성향.추가성향);
const similarDesc = computed(() => splitBySentence(props.pData.낭만성향.유사성향.추가설명));
const similarAnalysis = computed(() => splitBySentence(props.pData.낭만성향.유사성향.분석특징));
const nEIData = computed(() => props.pData.낭만성향.성향분포.EI);
const nNSData = computed(() => props.pData.낭만성향.성향분포.NS);
const nFTData = computed(() => props.pData.낭만성향.성향분포.FT);
const nPJData = computed(() => props.pData.낭만성향.성향분포.PJ);
const nFEData = computed(() => props.pData.낭만성향.성향분포.FE);
/** N코드 설명 열기/닫기 */
const openTooltip = () => {
  showTooltip.value = !showTooltip.value;
};
</script>

<template>
  <VRow class="full-height">
    <VCol cols="12" :md="halfCol" class="no-pad-col mobile-first">
      <VCard class="personality-card full-height right">
        <div class="detail-card__top-title personality" :class="{ mobile: isMobile }">
          <span class="detail-tit__num personality">2</span>
          <span class="detail-tit__txt personality">
            <span>명리체계로 분석한&nbsp;</span>
            <span>낭만만세력의 성향 종합 분석</span>
          </span>
          <div class="explain-nocde" :class="{ mobile: isMobile }" @click="openTooltip">
            NCODE란?
            <div v-if="showTooltip" class="explain-ncode__tooltip" :class="{ mobile: isMobile }">
              <div class="ncode-tooltip__top">
                이 분석 결과는 기본성향코드와 용신코드로 구성된
                <span class="n-keyword">낭만성향(NCODE)</span>을 바탕으로 자기이해를 돕기 위한
                통합적 도구로 설계되었습니다.
              </div>
              <div class="personality-divider" />
              <div class="ncode-tooltip__bottom">
                개인의 성향은 다양한 상황과 환경에 따라 유동적으로 발현될 수 있으며, 이는 자연스러운
                현상입니다. 이 분석을 통해 자기 자신을 더 깊이 이해하는 기회로 삼되, 결과를 절대적
                분류로 해석하기보다 성향 이해의 수단으로 활용하시기 바랍니다.
              </div>
            </div>
          </div>
        </div>
        <div class="personality-wrapper">
          <div class="personality-ncode">
            <span>중심성향</span>
            <NcodeLetters :myNcode="myNcode" />
          </div>
          <div class="personality-node__chart">
            <!-- MBTI 차트 테스트 중 -->
            <DivergingBarChart :chartData="nCodeChartData" />
            <div class="nocde-chat__plus-icon"></div>
            <SajuFiveChart :fiveChardData="fivePieChart" :isPersonal="true" :isMobile="isMobile" />
          </div>
          <div class="person-desc-box">
            <div class="desc__title">유사성향</div>
            <div class="desc__body">
              <div class="similar-personality" :class="{ mobile: isMobile }">
                <div class="similar-person-box">
                  <span>유사성향 1</span>
                  <div class="similar-person-code">
                    <NcodeLetters :myNcode="similarNcode[0]" />
                  </div>
                </div>
                <div class="similar-person-box">
                  <span>유사성향 2</span>
                  <div class="similar-person-code">
                    <NcodeLetters :myNcode="similarNcode[1]" />
                  </div>
                </div>
              </div>
              <div v-for="(sentence, idx) in similarDesc" :key="idx">
                <span class="content__body-txt">
                  <template v-for="(part, pIdx) in splitKeywordParts(sentence)" :key="pIdx">
                    <span v-if="part.isKeyword" class="p-keyword">{{ part.text }}</span>
                    <template v-else>{{ part.text }}</template>
                  </template>
                </span>
              </div>
            </div>
          </div>
          <div class="personality-divider" />
          <div class="person-desc-box">
            <div class="desc__title">분석특징</div>
            <div class="desc__body">
              <div v-for="(sentence, idx) in similarAnalysis" :key="idx">
                <span class="content__body-txt">
                  <template v-for="(part, pIdx) in splitKeywordParts(sentence)" :key="pIdx">
                    <span v-if="part.isKeyword" class="p-keyword">{{ part.text }}</span>
                    <template v-else>{{ part.text }}</template>
                  </template>
                </span>
              </div>
            </div>
          </div>
        </div>
      </VCard>
    </VCol>
    <VCol cols="12" :md="halfCol" class="no-pad-col mobile-second">
      <VCard class="personality-card full-height">
        <div class="personality-wrapper ncode">
          <NcodeDescribe :n-code-describe="nEIData" />
          <NcodeDescribe :n-code-describe="nNSData" />
          <NcodeDescribe :n-code-describe="nFTData" />
          <NcodeDescribe :n-code-describe="nPJData" />
          <div class="nocde-chat__plus-icon right"></div>
          <NcodeDescribe :n-code-describe="nFEData" :isFEData="true" :isMobile="isMobile" />
        </div>
      </VCard>
    </VCol>
  </VRow>
</template>
<style scoped lang="scss">
.nocde-chat__plus-icon {
  width: 32px;
  height: 32px;
  aspect-ratio: 1/1;
  background: url('@manseImages/plus-icon-primary.svg') center center no-repeat;
  background-size: contain;
  &.right {
    margin-bottom: 20px;
  }
}
</style>
